import type {ReactElement} from 'react';
import {useAppSelector, useAppDispatch} from '@/hooks';
import {getCurrentPageDuration} from '@Components/poster-editor/poster-editor-reducer';
import {MIN_PAGE_DURATION} from '@PosterWhiteboard/libraries/video-page';
import {useItemModifying} from '../../hooks/timeline-hooks';
import {setSelectedTimelineItem} from '../../timeline-reducer';
import {TimelineTrack} from '../timeline-track/timeline-track';
import {PageTimelineItem} from '../page-timeline-item/page-timeline-item';
import {TimelineItemType, MIN_ITEM_DURATION, PAGE_TRACK_HEIGHT} from '../../timeline.types';
import type {ResizeModifyingData} from '../timeline-item/timeline-item.types';

export function PageTimelineTrack(): ReactElement | null {
  const dispatch = useAppDispatch();

  const currentPageId = useAppSelector((state) => state.posterEditor.currentPageId);
  const currentPageDuration = useAppSelector(getCurrentPageDuration);

  const itemModifying = useItemModifying();

  if (currentPageDuration === undefined || !currentPageId) {
    return null;
  }

  const isResizing =
    itemModifying?.timelineItemType === TimelineItemType.PAGE &&
    itemModifying?.action.type === 'resizing' &&
    itemModifying?.itemId === currentPageId;

  const displayData = isResizing
    ? {
        id: currentPageId,
        startTime: itemModifying.startTime,
        endTime: itemModifying.endTime,
      }
    : {
        id: currentPageId,
        startTime: 0,
        endTime: currentPageDuration,
      };

  const handlePageClick = (id: string): void => {
    void window.posterEditor?.whiteboard?.pause();
    window.posterEditor?.whiteboard?.clearSelection();
    dispatch(setSelectedTimelineItem({type: TimelineItemType.PAGE, id}));
  };

  const handlePageResizeEnd = async (id: string, data: ResizeModifyingData): Promise<void> => {
    const pagesWhiteboard = window.posterEditor?.whiteboard?.pages.pagesHashMap;
    const page = pagesWhiteboard?.[id];

    if (page) {
      const {startTime, endTime} = data;
      const newDuration = Math.max(MIN_ITEM_DURATION, endTime - startTime);
      await page.updateFromObject({
        duration: Number(newDuration.toFixed(2)),
        isCustomDuration: true,
      });
    }
  };

  return (
    <TimelineTrack totalRows={1} trackHeight={PAGE_TRACK_HEIGHT}>
      <PageTimelineItem
        id={displayData.id}
        startTime={displayData.startTime}
        endTime={displayData.endTime}
        minPageDuration={MIN_PAGE_DURATION}
        subRow={0}
        onClick={handlePageClick}
        onResizeEnd={handlePageResizeEnd}
      />
    </TimelineTrack>
  );
}
